import React, { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'

type EmployeeLite = {
  first_name: string | null
  last_name: string | null
  employee_number?: string | null
}

type Failure = {
  id: string
  employee_id: string | null
  user_id?: string | null
  attempt_type: string
  reason: string
  distance_ft: number | null
  attempted_at: string
  employee?: EmployeeLite | null
}

type Prompt = {
  id: string
  employee_id: string | null
  prompt_type: string
  response: string | null
  triggered_at: string
  employee?: EmployeeLite | null
}

type EntryLite = {
  id: string
  employee_id: string | null
  entry_type?: string | null
  clock_in_time: string
  clock_out_time: string | null
  employee?: EmployeeLite | null
}

interface PunchAuditPanelProps {
  dateFrom: string
  dateTo: string
  employeeFilter: string
}

function normalizeEmployee(raw: unknown): EmployeeLite | null {
  if (!raw) return null
  const row = Array.isArray(raw) ? raw[0] : raw
  if (!row || typeof row !== 'object') return null
  const e = row as EmployeeLite
  return {
    first_name: e.first_name ?? null,
    last_name: e.last_name ?? null,
    employee_number: e.employee_number ?? null,
  }
}

function empName(e?: EmployeeLite | null) {
  if (!e) return '—'
  return `${e.first_name || ''} ${e.last_name || ''}`.trim() || '—'
}

function empNumber(e?: EmployeeLite | null) {
  if (e?.employee_number != null && String(e.employee_number).trim() !== '') {
    return String(e.employee_number).trim()
  }
  return '—'
}

async function resolveEmployeesByUserIds(userIds: string[]): Promise<Map<string, EmployeeLite>> {
  const unique = Array.from(new Set(userIds.filter(Boolean)))
  if (unique.length === 0) return new Map()
  const { data, error } = await supabase
    .from('employees')
    .select('user_profile_id, first_name, last_name, employee_number')
    .in('user_profile_id', unique)
  if (error || !data) return new Map()
  const map = new Map<string, EmployeeLite>()
  for (const row of data) {
    if (row.user_profile_id) {
      map.set(row.user_profile_id, {
        first_name: row.first_name ?? null,
        last_name: row.last_name ?? null,
        employee_number: row.employee_number ?? null,
      })
    }
  }
  return map
}

export function PunchAuditPanel({ dateFrom, dateTo, employeeFilter }: PunchAuditPanelProps) {
  const [showAll, setShowAll] = useState(false)
  const [failures, setFailures] = useState<Failure[]>([])
  const [prompts, setPrompts] = useState<Prompt[]>([])
  const [entries, setEntries] = useState<EntryLite[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        let failQ = supabase
          .from('time_punch_failures')
          .select(
            'id, employee_id, user_id, attempt_type, reason, distance_ft, attempted_at, employee:employees(first_name, last_name, employee_number)'
          )
          .order('attempted_at', { ascending: false })
          .limit(200)
        if (dateFrom) failQ = failQ.gte('attempted_at', `${dateFrom}T00:00:00`)
        if (dateTo) failQ = failQ.lte('attempted_at', `${dateTo}T23:59:59`)
        if (employeeFilter !== 'all') failQ = failQ.eq('employee_id', employeeFilter)

        let promptQ = supabase
          .from('time_day_prompts')
          .select(
            'id, employee_id, prompt_type, response, triggered_at, employee:employees(first_name, last_name, employee_number)'
          )
          .order('triggered_at', { ascending: false })
          .limit(100)
        if (dateFrom) promptQ = promptQ.gte('triggered_at', `${dateFrom}T00:00:00`)
        if (dateTo) promptQ = promptQ.lte('triggered_at', `${dateTo}T23:59:59`)
        if (employeeFilter !== 'all') promptQ = promptQ.eq('employee_id', employeeFilter)

        let entryQ = supabase
          .from('time_entries')
          .select(
            'id, employee_id, entry_type, clock_in_time, clock_out_time, employee:employees(first_name, last_name, employee_number)'
          )
          .order('clock_in_time', { ascending: false })
          .limit(200)
        if (dateFrom) entryQ = entryQ.gte('clock_in_time', `${dateFrom}T00:00:00`)
        if (dateTo) entryQ = entryQ.lte('clock_in_time', `${dateTo}T23:59:59`)
        if (employeeFilter !== 'all') entryQ = entryQ.eq('employee_id', employeeFilter)

        const [fRes, pRes, eRes] = await Promise.all([failQ, promptQ, entryQ])
        if (fRes.error) throw fRes.error
        if (pRes.error) throw pRes.error
        if (eRes.error) throw eRes.error
        if (cancelled) return

        const rawFailures = (fRes.data || []) as unknown as Array<Failure & { employee?: unknown }>
        const missingUserIds = rawFailures
          .filter((f) => !normalizeEmployee(f.employee) && f.user_id)
          .map((f) => f.user_id as string)
        const byUserId = await resolveEmployeesByUserIds(missingUserIds)

        const normalizedFailures: Failure[] = rawFailures.map((f) => {
          let employee = normalizeEmployee(f.employee)
          if (!employee && f.user_id) employee = byUserId.get(f.user_id) ?? null
          return { ...f, employee }
        })
        const normalizedPrompts: Prompt[] = ((pRes.data || []) as unknown as Array<Prompt & { employee?: unknown }>).map(
          (p) => ({ ...p, employee: normalizeEmployee(p.employee) })
        )
        const normalizedEntries: EntryLite[] = ((eRes.data || []) as unknown as Array<EntryLite & { employee?: unknown }>).map(
          (e) => ({ ...e, employee: normalizeEmployee(e.employee) })
        )

        setFailures(normalizedFailures)
        setPrompts(normalizedPrompts)
        setEntries(normalizedEntries)
      } catch (e) {
        if (!cancelled) {
          const msg =
            e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
              ? (e as { message: string }).message
              : e instanceof Error
                ? e.message
                : 'Failed to load punch audit'
          setError(msg)
          setFailures([])
          setPrompts([])
          setEntries([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dateFrom, dateTo, employeeFilter])

  const timeline = showAll
    ? [
        ...entries.map((e) => ({
          kind: 'ok' as const,
          at: e.clock_in_time,
          id: `ok-${e.id}`,
          employee: e.employee,
          label: `punched ${e.entry_type || 'job'} in`,
          detail: e.clock_out_time
            ? `Out ${new Date(e.clock_out_time).toLocaleString()}`
            : 'Still open',
        })),
        ...failures.map((f) => ({
          kind: 'fail' as const,
          at: f.attempted_at,
          id: `fail-${f.id}`,
          employee: f.employee,
          label: `failed ${f.attempt_type}`,
          detail: `${f.reason}${f.distance_ft != null ? ` · ${Math.round(f.distance_ft)} ft` : ''}`,
        })),
      ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
    : failures.map((f) => ({
        kind: 'fail' as const,
        at: f.attempted_at,
        id: `fail-${f.id}`,
        employee: f.employee,
        label: `failed ${f.attempt_type}`,
        detail: `${f.reason}${f.distance_ft != null ? ` · ${Math.round(f.distance_ft)} ft` : ''}`,
      }))

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Punch audit</h2>
          <p className="text-sm text-gray-500">Failed punches and day-end prompts for the selected range</p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          All punch events
        </label>
      </div>

      {error && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          {error}
          {/does not exist|schema cache|Could not find the table/i.test(error) && (
            <span className="block text-amber-700 mt-1">
              If tables are missing, run migration 202607231800_time_qr_punch_audit.sql.
            </span>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading audit…</p>
      ) : (
        <>
          {prompts.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm font-semibold text-amber-900 mb-2">Day-end prompts</p>
              <ul className="space-y-1 text-sm text-amber-900">
                {prompts.slice(0, 12).map((p) => (
                  <li key={p.id}>
                    {empNumber(p.employee) !== '—' ? `#${empNumber(p.employee)}` : '—'} · {empName(p.employee)} · {p.prompt_type} → {p.response || '—'} ·{' '}
                    {new Date(p.triggered_at).toLocaleString()}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4 font-medium">When</th>
                  <th className="py-2 pr-4 font-medium">Emp #</th>
                  <th className="py-2 pr-4 font-medium">Employee</th>
                  <th className="py-2 pr-4 font-medium">Event</th>
                  <th className="py-2 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {timeline.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-6 text-gray-500">
                      No punch failures{showAll ? ' or entries' : ''} in this range.
                    </td>
                  </tr>
                ) : (
                  timeline.map((row) => (
                    <tr key={row.id} className="border-b border-gray-100">
                      <td className="py-2 pr-4 whitespace-nowrap text-gray-600">
                        {new Date(row.at).toLocaleString()}
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap text-gray-700 font-medium">
                        {empNumber(row.employee) !== '—' ? `#${empNumber(row.employee)}` : '—'}
                      </td>
                      <td className="py-2 pr-4 text-gray-900">{empName(row.employee)}</td>
                      <td className="py-2 pr-4">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            row.kind === 'fail'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {row.kind === 'fail' ? 'Failed' : 'OK'}
                        </span>{' '}
                        {row.label}
                      </td>
                      <td className="py-2 text-gray-600">{row.detail}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

export default PunchAuditPanel
